/**
 * Requirement Templates hook — loads the account's templates once on mount.
 * Templates are read-mostly; the API auto-seeds system defaults on first call.
 */
import { useEffect, useState } from 'react'
import { requirementsApi } from '@/lib/api/client'
import type { RequirementTemplate, RequirementType } from '@ispot/shared'

interface State {
  templates: RequirementTemplate[]
  isLoading: boolean
  error: string | null
}

export function useRequirementTemplates(accountId: string, projectId: string) {
  const [state, setState] = useState<State>({ templates: [], isLoading: false, error: null })

  useEffect(() => {
    let cancelled = false
    setState(s => ({ ...s, isLoading: true, error: null }))

    requirementsApi.listTemplates(accountId, projectId)
      .then((res) => {
        if (cancelled) return
        const templates = (res?.templates ?? []) as RequirementTemplate[]
        setState({ templates, isLoading: false, error: null })
      })
      .catch((err: Error) => {
        if (cancelled) return
        setState({ templates: [], isLoading: false, error: err.message })
      })

    return () => { cancelled = true }
  }, [accountId, projectId])

  /** Resolve the default template for a requirement type. */
  function defaultFor(type: RequirementType): RequirementTemplate | null {
    return (
      state.templates.find(t => t.appliesTo === type && t.isDefault)
      ?? state.templates.find(t => t.appliesTo === type)
      ?? null
    )
  }

  return { ...state, defaultFor }
}
